import { Bell, Menu, ChevronDown, LogOut, UserRound } from "lucide-react";
import { Link } from "react-router-dom";
import { Sheet, SheetContent, SheetTrigger, SheetTitle, SheetDescription } from "./ui/sheet";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "./ui/dropdown-menu";
import { Popover, PopoverContent, PopoverTrigger } from "./ui/popover";
import { Button } from "./ui/button";
import { CallistoLogo } from "./CallistoLogo";
import { useAuth } from "../context/AuthContext";
import { useEvents } from "../context/EventsContext";
import Sidebar from "./Sidebar";
import { useState } from "react";
// The role names are `lib/roleOptions`'s, not this header's. The local copy
// held here was byte-identical to that one — a second list of the words the
// company uses for its own people, one edit away from disagreeing with the
// Team page's badges.
import { ROLE_LABEL, userInitials } from "../lib/roleOptions";
import { formatISTDateTime, formatISTTime, istDayKey, sameISTDay } from "../lib/datetime";
import { joinParts } from "../lib/vocabulary";

// One line per event in the bell panel. `detail_url` shape mirrors the
// approvals queue: leaves/expenses/tour plans have detail pages, the rest
// render as text.
const EVENT_URL = {
  expense: (id) => `/expenses/${id}`,
  leave: (id) => `/leaves/${id}`,
  tour_plan: (id) => `/route-planner/${id}`,
};

/**
 * How long ago an event happened — relative for the last hour, then the clock.
 *
 * RELATIVE IS FINE, VAGUE IS NOT (RC-8). "5m ago" is a subtraction of two
 * instants and carries no zone, so it was correct; "17h ago" and "3d ago" were
 * the problem — they are the only thing on screen about WHEN, and they leave
 * the reader to do IST arithmetic against a number the app already knows. Past
 * the hour this hands over to the one clock, which names its zone.
 */
function eventTime(iso) {
  const then = istDayKey(iso) ? new Date(iso) : null;
  if (!then || Number.isNaN(then.getTime())) return "";
  const mins = Math.floor((Date.now() - then.getTime()) / 60000);
  if (mins >= 0 && mins < 60) return `${mins}m ago`;
  return sameISTDay(then, new Date()) ? formatISTTime(then) : formatISTDateTime(then);
}

export default function Header() {
  const { user, logout } = useAuth();
  const [openSheet, setOpenSheet] = useState(false);
  const { events, unread, markAllRead } = useEvents();

  const initials = userInitials(user?.full_name);

  return (
    // `pt-safe` is the status-bar clearance and `min-h-[...]` grows the header
    // by the same inset, so the 4rem of chrome below the notch is unchanged and
    // nothing that measures against the header's height goes out of step. Both
    // read `--safe-area-top`, which is 0px on desktop — the layout there is
    // byte-for-byte what it was.
    <header
      className="bg-white border-b border-callisto flex items-center justify-between px-4 md:px-6 sticky top-0 z-30 pt-safe min-h-[calc(4rem+var(--safe-area-top))]"
      data-testid="app-shell-header"
    >
      <div className="flex items-center gap-3">
        <Sheet open={openSheet} onOpenChange={setOpenSheet}>
          <SheetTrigger asChild>
            <Button
              variant="ghost"
              size="icon"
              className="md:hidden text-callisto-navy"
              data-testid="mobile-menu-trigger"
              aria-label="Open menu"
            >
              <Menu className="h-5 w-5" />
            </Button>
          </SheetTrigger>
          <SheetContent side="left" className="p-0 w-72 max-w-[85vw] bg-callisto-navy border-0">
            <SheetTitle className="sr-only">Navigation</SheetTitle>
            <SheetDescription className="sr-only">Sidebar navigation menu</SheetDescription>
            <Sidebar onNavigate={() => setOpenSheet(false)} />
          </SheetContent>
        </Sheet>

        <div className="md:hidden">
          <CallistoLogo variant="light" size="sm" />
        </div>
        <div className="hidden md:block">
          <p className="text-xs uppercase tracking-[0.2em] text-callisto-muted">
            Callisto Pharmaceuticals
          </p>
          <h1 className="text-base font-semibold text-callisto-navy">SFA Workspace</h1>
        </div>
      </div>

      <div className="flex items-center gap-2">
        <Popover
          onOpenChange={(open) => {
            if (open) markAllRead();
          }}
        >
          <PopoverTrigger asChild>
            <button
              type="button"
              className="relative h-9 w-9 rounded-full hover:bg-callisto-app flex items-center justify-center text-callisto-navy"
              data-testid="notification-bell"
              aria-label={unread > 0 ? `Notifications (${unread} unread)` : "Notifications"}
            >
              <Bell className="h-5 w-5" />
              {/* The dot means UNREAD EVENTS — it was unconditional once,
                  which is a light that is always on and therefore never on. */}
              {unread > 0 && (
                <span
                  className="absolute top-2 right-2 h-2 w-2 rounded-full bg-callisto-gold"
                  data-testid="notification-unread-dot"
                />
              )}
            </button>
          </PopoverTrigger>
          <PopoverContent align="end" className="w-80 p-0" data-testid="notification-panel">
            <div className="px-4 py-2 border-b border-callisto text-sm font-semibold text-callisto-navy">
              Notifications
            </div>
            <div className="max-h-80 overflow-y-auto">
              {events.length === 0 && (
                <p
                  className="px-4 py-6 text-sm text-callisto-muted"
                  data-testid="notification-empty"
                >
                  Nothing yet — approvals that need you, and decisions on your
                  claims, land here.
                </p>
              )}
              {events.map((ev) => {
                const url = EVENT_URL[ev.module]?.(ev.ref_id);
                const body = (
                  <div className="px-4 py-2.5 border-b border-callisto/60 last:border-b-0 hover:bg-callisto-app">
                    <p className="text-sm text-callisto-navy">{ev.title}</p>
                    {ev.body && (
                      <p className="text-xs text-callisto-muted truncate">{ev.body}</p>
                    )}
                    <p className="text-[11px] text-callisto-muted mt-0.5">
                      {joinParts([ev.actor_name, eventTime(ev.created_at)])}
                    </p>
                  </div>
                );
                return url ? (
                  <Link key={ev.id} to={url} data-testid={`notification-item-${ev.id}`}>
                    {body}
                  </Link>
                ) : (
                  <div key={ev.id} data-testid={`notification-item-${ev.id}`}>
                    {body}
                  </div>
                );
              })}
            </div>
          </PopoverContent>
        </Popover>

        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button
              type="button"
              className="flex items-center gap-2 rounded-full pl-1 pr-2 py-1 hover:bg-callisto-app"
              data-testid="user-profile-dropdown"
            >
              <span className="h-8 w-8 rounded-full bg-callisto-navy text-white text-xs font-semibold flex items-center justify-center">
                {initials}
              </span>
              <span className="hidden sm:flex flex-col items-start leading-tight pr-1">
                <span className="text-sm font-semibold text-callisto-navy max-w-[140px] truncate">
                  {user?.full_name}
                </span>
                <span className="text-[11px] text-callisto-gold">
                  {ROLE_LABEL[user?.role] || user?.role}
                </span>
              </span>
              <ChevronDown className="h-4 w-4 text-callisto-muted" />
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-56">
            <DropdownMenuLabel className="flex flex-col">
              <span className="font-semibold text-callisto-navy">{user?.full_name}</span>
              <span className="text-xs text-callisto-muted truncate">{user?.email}</span>
            </DropdownMenuLabel>
            <DropdownMenuSeparator />
            <DropdownMenuItem className="cursor-not-allowed opacity-60">
              <UserRound className="mr-2 h-4 w-4" />
              Profile (coming soon)
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={logout} data-testid="header-logout-button">
              <LogOut className="mr-2 h-4 w-4" />
              Log out
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </header>
  );
}
